import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { fetchAppealByOrgAndAdminUnit } from "services/complaintDetailService";
import { Appeal, AppealStatus, Roles } from "types";
import { ITEMS_PER_PAGE } from "utils/constant";
import { DateView, errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";
import { Gavel } from "lucide-react";

// Tabs for display
const tab_for_all = [
  { key: "All", label: "All" },
  { key: "pending", label: AppealStatus.pending },
  { key: "on_appointment", label: AppealStatus.on_appointment },
  { key: "resolved", label: AppealStatus.resolved },
  { key: "appealed", label: AppealStatus.appealed },
];
const tab_for_registrar = [
  { key: "All", label: "All" },
  { key: "pending", label: AppealStatus.pending },
  { key: "registered", label: "Registered" },
];

const AppealList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState(tab_for_all);

  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);

  const fetchData = () => {
    setLoading(true);
    fetchAppealByOrgAndAdminUnit(
      loginData?.admin_unit ?? "",
      loginData?.org_unit ?? "",
      isRegistrar,
    )
      .then((data) => {
        setAppeals(
          data?.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          ) || [],
        );
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      fetchData();
      if (isRegistrar) {
        setTabs(tab_for_registrar);
      }
    }
  }, [activeTab, loginData]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = appeals.filter((appeal) => {
    // 1️⃣ — filter by status tab
    if (activeTab !== "All") {
      const tabKey = tabs.find((tab) => tab.label === activeTab)?.key;

      // Special handling for pending tab based on user role
      if (tabKey === "pending") {
        // For registrar role - show only forwarded_to_registrar status
        if (isRegistrar) {
          if (!["forwarded_to_registrar"].includes(appeal.status ?? "")) {
            return false;
          }
        }
        // For non-registrar roles - show pending and forwarded_to_management
        else {
          if (
            ![
              "pending",
              "forwarded_to_management",
              "forwarded_to_registrar",
            ].includes(appeal.status ?? "")
          ) {
            return false;
          }
        }
      }
      // Handle registered tab - filter by presence of registration_number
      else if (tabKey === "on_appointment") {
        // Show only complaints that have a registration number
        if (!["on_appointment"].includes(appeal.status ?? "")) {
          return false;
        }
      } else if (tabKey === "resolved") {
        if (!["resolved"].includes(appeal.status ?? "")) {
          return false;
        }
      } else if (tabKey === "registered") {
        if (!["resolved"].includes(appeal.status ?? "")) {
          return false;
        }
      } else if (tabKey === "appealed") {
        if (
          appeal?.child_appeals_info &&
          appeal?.child_appeals_info?.length <= 0
        ) {
          return false;
        }
      }
    }

    const id = appeal?.id?.toString()?.toLowerCase() || "";
    const summary = appeal?.appeal_summary?.toLowerCase() || "";
    const status = appeal?.status?.toString()?.toLowerCase() || "";
    const orgUnit = appeal?.org_unit_id?.toLowerCase() || "";

    const matchesText =
      id.includes(searchTerm) ||
      summary.includes(searchTerm) ||
      status.includes(searchTerm) ||
      orgUnit.includes(searchTerm);

    return matchesText;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate paginated incidents
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedData = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Appeals"
        icon={FaBriefcase}
        tabs={tabs?.map((t) => t?.label)}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={
          <>
            {/* {isClerk && (
              <button
                onClick={() => navigate("request")}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                New Appeal
              </button>
            )} */}
          </>
        }
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Appeals" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title="Go Back"
                        >
                          ◀️
                        </button>
                        <span>Appeal Table</span>
                      </div>
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search"
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">Compliant Req</th>
                  <th className="p-3 text-left">Client Name</th>
                  <th className="p-3 text-left">Appeal Date</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    View Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((data, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500]  flex flex-row items-center gap-2">
                      <img
                        src="/complient.png"
                        alt="complient"
                        className="w-10 h-10"
                      />
                      {data?.complaint_info?.client_id ?? "N/A"}
                    </td>
                    <td className="p-3 font-[500]">
                      {data?.complaint_info?.client_full_name ?? "N/A"}
                    </td>
                    {/* <td className="p-3 font-[500]">
                      {data?.complaint_info?.id ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/complaint/${data?.complaint_info?.id}`)
                          }
                          className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                          title="Go to Complaint Detail"
                        >
                          View
                        </button>
                      ) : (
                        <span className="">N/A</span>
                      )}
                    </td> */}

                    <td className="p-3 font-[500]">
                      {DateView(data.appeal_date) || "N/A"}
                    </td>
                    <td className="p-3 font-[500]">
                      <span className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
                        {data?.status && (
                          <span
                            className={`px-4 py-1 rounded-full text-sm font-bold ${
                              data.status === "resolved"
                                ? "bg-green-100 text-green-700 border border-green-300"
                                : data.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                  : data.status === "closed"
                                    ? "bg-gray-200 text-gray-700 border border-gray-300"
                                    : data.status === "forwarded_to_management"
                                      ? "bg-red-100 text-red-700 border border-red-300"
                                      : data.status === "appealed"
                                        ? "bg-red-100 text-red-700 border border-red-300"
                                        : "bg-gray-100 text-gray-700 border border-gray-300"
                            }
              `}
                          >
                            {AppealStatus[data.status]}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="p-3 font-[500]">
                      <div className="flex flex-row items-center align-middle gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigate(data?.id + "/");
                          }}
                          className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                        >
                          View
                        </button>
                        <div className="p-3 font-[500]">
                          {data?.is_appealed ? (
                            <Gavel className="w-5 h-5 text-red-600" />
                          ) : (
                            <Gavel className="w-5 h-5 text-gray-300" />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      No appeals found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppealList;
